import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { OFFICE_TYPES } from "../../../dto/Company/CompanySettingsDto";
import AccountHistoryModel from "../../../viewModels/Deal/AccountHistoryModel";
import DealHistoryModel from "../../../viewModels/Deal/DealHistoryModel";
import DealModel, { DealDataConverter } from "../../../viewModels/Deal/DealModel";
import { DealRoutesDto } from "../../../dto/Deal/DealDto";
import PaymentModel from "../../../viewModels/Payments/PaymentModel";

type LoadAdminDealPayload = {
  id: number;
  officeType: OFFICE_TYPES;
};

const initialState: {
  loading: boolean;
  loadingData: boolean;
  loadingError: boolean;
  currentRequest?: LoadAdminDealPayload;
  routesRequested: boolean;
  routesLoading: boolean;
  routesError: boolean;

  deal?: DealModel;
  paymentsInAccounts?: Array<PaymentModel>;

  accountHistoryModalOpen: boolean;
  accountHistoryModalLoading: boolean;
  accountHistoryModalLoadingData: boolean;
  accountIdToShowHistory?: number;
  accountHistoryPage: number;
  accountHistoryPageSize: number;
  accountHistories?: Array<AccountHistoryModel>;
  accountHistoriesTotal?: number;

  dealHistoryModalOpen: boolean;
  dealHistoryModalLoading: boolean;
  dealHistoryModalLoadingData: boolean;
  dealHistoryPage: number;
  dealHistoryPageSize: number;
  dealHistories?: Array<DealHistoryModel>;
  dealHistoriesTotal?: number;
} = {
  loading: true,
  loadingData: false,
  loadingError: false,
  routesRequested: false,
  routesLoading: false,
  routesError: false,

  accountHistoryModalOpen: false,
  accountHistoryModalLoading: false,
  accountHistoryModalLoadingData: false,
  accountHistoryPage: 1,
  accountHistoryPageSize: 10,

  dealHistoryModalOpen: false,
  dealHistoryModalLoading: false,
  dealHistoryModalLoadingData: false,
  dealHistoryPage: 1,
  dealHistoryPageSize: 10,
};

const adminDealSlice = createSlice({
  name: "adminPanelDeal",
  initialState,
  reducers: {
    loadAdminDeal(state, action: PayloadAction<LoadAdminDealPayload>) {
      if (state.currentRequest?.id !== action.payload.id || state.currentRequest?.officeType !== action.payload.officeType) {
        return { ...initialState, currentRequest: action.payload, loadingData: true };
      }
      state.loading = !state.deal;
      state.loadingData = true;
      state.loadingError = false;
    },
    loadAdminDealSuccess(state, action: PayloadAction<{ deal: DealModel; paymentsInAccounts: Array<PaymentModel> }>) {
      state.loading = false;
      state.loadingData = false;
      state.loadingError = false;
      state.deal = DealDataConverter.preserveDealRoutes(action.payload.deal, state.deal);
      state.paymentsInAccounts = action.payload.paymentsInAccounts;
    },
    loadAdminDealReject(state) {
      state.loading = false;
      state.loadingData = false;
      state.loadingError = true;
    },

    loadAdminDealRoutes(state, action: PayloadAction<LoadAdminDealPayload>) {
      if (
        state.routesRequested ||
        !state.deal ||
        state.currentRequest?.id !== action.payload.id ||
        state.currentRequest?.officeType !== action.payload.officeType
      ) {
        return;
      }
      state.routesRequested = true;
      state.routesLoading = true;
      state.routesError = false;
    },
    loadAdminDealRoutesSuccess(state, action: PayloadAction<{ request: LoadAdminDealPayload; routes: DealRoutesDto }>) {
      if (
        !state.deal ||
        state.currentRequest?.id !== action.payload.request.id ||
        state.currentRequest?.officeType !== action.payload.request.officeType ||
        action.payload.routes.dealId !== state.deal.id
      ) {
        return;
      }
      state.deal = DealDataConverter.applyDealRoutes(state.deal, action.payload.routes);
      state.routesLoading = false;
      state.routesError = false;
    },
    loadAdminDealRoutesReject(state, action: PayloadAction<LoadAdminDealPayload>) {
      if (state.currentRequest?.id !== action.payload.id || state.currentRequest?.officeType !== action.payload.officeType) return;
      state.routesLoading = false;
      state.routesError = true;
    },

    resetState: () => initialState,

    openAccountHistoryModal(state, action: PayloadAction<number>) {
      state.accountHistoryModalOpen = true;
      state.accountHistoryModalLoading = true;
      state.accountHistoryModalLoadingData = true;
      state.accountIdToShowHistory = action.payload;
      state.accountHistoryPage = 1;
    },
    closeAccountHistoryModal(state) {
      state.accountHistoryModalOpen = false;
      state.accountHistoryModalLoading = false;
      state.accountHistoryModalLoadingData = false;
      state.accountIdToShowHistory = undefined;
      state.accountHistoriesTotal = undefined;
    },
    changeAccountHistoryTable(state, action: PayloadAction<{ page: number; pageSize: number }>) {
      state.accountHistoryModalLoadingData = true;
      state.accountHistoryPage = action.payload.page;
      state.accountHistoryPageSize = action.payload.pageSize;
    },
    loadAccountHistorySuccess(state, action: PayloadAction<{ accountHistories: Array<AccountHistoryModel>; total: number }>) {
      state.accountHistoryModalLoading = false;
      state.accountHistoryModalLoadingData = false;
      state.accountHistories = action.payload.accountHistories;
      state.accountHistoriesTotal = action.payload.total;
    },
    loadAccountHistoryReject(state) {
      state.accountHistoryModalLoading = false;
      state.accountHistoryModalLoadingData = false;
    },

    openDealHistoryModal(state) {
      state.dealHistoryModalOpen = true;
      state.dealHistoryModalLoading = true;
      state.dealHistoryModalLoadingData = true;
      state.dealHistoryPage = 1;
    },
    closeDealHistoryModal(state) {
      state.dealHistoryModalOpen = false;
      state.dealHistoryModalLoading = false;
      state.dealHistoryModalLoadingData = false;
      state.dealHistoriesTotal = undefined;
    },
    changeDealHistoryTable(state, action: PayloadAction<{ page: number; pageSize: number }>) {
      state.dealHistoryModalLoadingData = true;
      state.dealHistoryPage = action.payload.page;
      state.dealHistoryPageSize = action.payload.pageSize;
    },
    loadDealHistorySuccess(state, action: PayloadAction<{ dealHistories: Array<DealHistoryModel>; total: number }>) {
      state.dealHistoryModalLoading = false;
      state.dealHistoryModalLoadingData = false;
      state.dealHistories = action.payload.dealHistories;
      state.dealHistoriesTotal = action.payload.total;
    },
    loadDealHistoryReject(state) {
      state.dealHistoryModalLoading = false;
      state.dealHistoryModalLoadingData = false;
    },
  },
});

export default adminDealSlice;
